import { useEffect, useRef, useState } from "react";
import { BarChart, Bar, XAxis, YAxis } from "recharts";

export const BAR_MIN_WIDTH = 120;
export const THUMB_SIZE = 96;
export const CHART_HEIGHT = 420;
export const CHART_MARGIN = { top: 24, right: 20, bottom: 4, left: 20 };
export const XAXIS_HEIGHT = 10;
export const YAXIS_WIDTH = 48;

/** Total width of the fixed/"sticky" Y-axis column, including its left margin. */
export const FIXED_YAXIS_WIDTH = YAXIS_WIDTH + CHART_MARGIN.left;

/**
 * Computes "nice", evenly-spaced Y-axis tick values that always start at 0 — a bar's
 * height must be proportional to its actual value, so the baseline can't float above
 * zero the way Recharts' default `["auto", "auto"]` domain would allow. Recharts'
 * own automatic tick generation can silently drop the boundary tick (0) depending on
 * the data range, so instead of trusting `domain={[0, "auto"]}` + auto ticks, we
 * compute the tick set ourselves and pass it explicitly via the `ticks` prop —
 * guaranteeing 0 always renders, and that the sticky axis chart and the scrollable
 * chart's hidden domain-matching axis always agree (both call this on the same data).
 */
// Standard d3-array `tickStep` rounding thresholds (sqrt(50), sqrt(10), sqrt(2)) —
// picks whichever of ×1/×2/×5/×10 keeps the resulting step closest to the rough
// per-tick step implied by `tickCount`, which is what makes the output "nice"
// (round numbers) rather than arbitrary fractions.
const E10 = Math.sqrt(50);
const E5 = Math.sqrt(10);
const E2 = Math.sqrt(2);

export function computeYAxisTicks(data: Array<{ value: number }>, tickCount = 5): number[] {
  const maxValue = data.reduce((max, d) => Math.max(max, d.value), 0);
  if (maxValue <= 0) return [0, 1];
  const roughStep = maxValue / Math.max(1, tickCount - 1);
  const power = Math.floor(Math.log10(roughStep));
  let step = 10 ** power;
  const error = roughStep / step;
  if (error >= E10) step *= 10;
  else if (error >= E5) step *= 5;
  else if (error >= E2) step *= 2;
  const niceMax = Math.ceil(maxValue / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= niceMax + step / 2; v += step) {
    ticks.push(Math.round(v / step) * step);
  }
  return ticks;
}

export function DrilldownActiveBar(props: Record<string, unknown>) {
  const x = Number(props.x ?? 0);
  const y = Number(props.y ?? 0);
  const width = Number(props.width ?? 0);
  const height = Number(props.height ?? 0);
  if (!width || !height) return null;
  const expand = 4;
  const lift = Math.round(height * 0.03);
  return (
    <rect
      x={x - expand}
      y={y - lift}
      width={width + expand * 2}
      height={height + lift}
      rx={8}
      ry={8}
      fill="#6c4bff"
      fillOpacity={1}
    />
  );
}

/** Tracks the width of a wrapper element via ResizeObserver — drives responsive chart width. */
export function useChartContainerWidth() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => {
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return { wrapperRef, containerWidth };
}

interface UseChartScrollPaginationArgs {
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

/**
 * Horizontal-scroll right-edge trigger for pagination.
 * Scroll-event-only — avoids the infinite-fetch loop that IntersectionObserver
 * causes (it fires immediately on every re-render when the sentinel is in view).
 */
export function useChartScrollPagination({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: UseChartScrollPaginationArgs) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingRef = useRef(isFetchingNextPage);
  const fetchNextPageRef = useRef(fetchNextPage);
  hasNextPageRef.current = hasNextPage;
  isFetchingRef.current = isFetchingNextPage;
  fetchNextPageRef.current = fetchNextPage;

  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root) return;
    const handleScroll = () => {
      if (!hasNextPageRef.current || isFetchingRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = root;
      if (scrollWidth - scrollLeft - clientWidth < 150) {
        fetchNextPageRef.current();
      }
    };
    root.addEventListener("scroll", handleScroll, { passive: true });
    return () => root.removeEventListener("scroll", handleScroll);
  }, []);

  return scrollContainerRef;
}

interface StickyYAxisChartProps {
  data: Array<{ value: number }>;
  tickFormatter: (value: number) => string;
}

/**
 * A narrow, non-scrolling BarChart rendering only the Y axis — rendered as a fixed
 * sibling to the left of the horizontally-scrolling bar chart, so the axis stays put
 * ("sticky") while the bars/thumbnails scroll underneath. Fed the same `data` (keyed
 * on `value`, same as the scrollable chart's Bar) so `computeYAxisTicks` derives an
 * identical tick set/domain in both charts, and includes the same hidden `XAxis`
 * height reservation as the scrollable chart so the two plot areas are pixel-identical
 * — otherwise the sticky ticks would sit a few pixels off from where the bars actually
 * are. The Bar here is fully transparent and never meant to be seen — it only exists
 * so Recharts lays out a real plot area to hang the axis on.
 */
export function StickyYAxisChart({ data, tickFormatter }: StickyYAxisChartProps) {
  const ticks = computeYAxisTicks(data);
  const domain: [number, number] = [0, ticks[ticks.length - 1]!];
  return (
    <BarChart
      width={FIXED_YAXIS_WIDTH}
      height={CHART_HEIGHT}
      data={data}
      margin={{ ...CHART_MARGIN, right: 0 }}
    >
      <XAxis dataKey="value" height={XAXIS_HEIGHT} tick={false} tickLine={false} axisLine={false} />
      <YAxis
        orientation="left"
        domain={domain}
        ticks={ticks}
        tickFormatter={tickFormatter}
        tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
        tickLine={false}
        axisLine={false}
        width={YAXIS_WIDTH}
      />
      <Bar dataKey="value" fill="transparent" isAnimationActive={false} />
    </BarChart>
  );
}

/**
 * Width math for the scrollable half of a sticky-Y-axis chart. `containerWidth` is
 * the wrapper's full measured width (from `useChartContainerWidth`) — the fixed axis
 * column is carved out of it first, so the two columns together fill the wrapper
 * exactly when there's room, and only the scrollable column grows/scrolls once the
 * bars need more space than that.
 */
export function useStickyChartWidth(containerWidth: number, dataLength: number) {
  const innerPad = CHART_MARGIN.left + CHART_MARGIN.right + YAXIS_WIDTH;
  const dataWidth = dataLength * BAR_MIN_WIDTH + innerPad;
  const availableScrollWidth = containerWidth > 0 ? Math.max(containerWidth - FIXED_YAXIS_WIDTH, 0) : 0;
  const chartWidth = availableScrollWidth > 0 ? Math.max(availableScrollWidth, dataWidth) : dataWidth;
  const plotAreaWidth = Math.max(0, chartWidth - CHART_MARGIN.left - YAXIS_WIDTH - CHART_MARGIN.right);
  return { chartWidth, plotAreaWidth };
}
